/**
 * Converts any value into a safe, human-readable label.
 *
 * PrimeNG select / multiselect emit the whole option object when the bound
 * `optionValue` key is missing or does not match the option shape. Rendering
 * those objects directly produces the literal text "[object Object]", so every
 * header/summary interpolation must go through this helper.
 */
export function toDisplayLabel(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) return fallback;

  if (typeof value === 'string') return value.trim() || fallback;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  if (Array.isArray(value)) {
    const labels = value.map(v => toDisplayLabel(v)).filter(Boolean);
    return labels.length ? labels.join(', ') : fallback;
  }

  if (value instanceof Date) return value.toLocaleDateString();

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    // PrimeNG option shapes, in priority order.
    for (const key of ['label', 'name', 'title', 'display', 'text', 'value', 'code', 'id']) {
      const candidate = obj[key];
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
      if (typeof candidate === 'number') return String(candidate);
    }
    try {
      const json = JSON.stringify(value);
      return json && json !== '{}' ? json : fallback;
    } catch {
      return fallback;
    }
  }

  return fallback;
}

/** True when the value carries no meaningful filter content. */
export function isBlankValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}
